import React from 'react';
import { BudgetGroup, CycleUtilisationCard } from '../components/budget.jsx';
import { formatCycleId } from '../core/dateHelpers.js';

// =============================================
// BUDGET SECTION COMPONENT
// =============================================
export function BudgetSection({
    // Budget cycles
    sgBudgetCycle1,
    setSgBudgetCycle1,
    sgBudgetCycle2,
    setSgBudgetCycle2,
    lkBudgetDamith,
    setLkBudgetDamith,
    lkBudgetShan,
    setLkBudgetShan,
    // Cycle management
    currentCycle,
    cycleOpenedAt,
    isNewCycle,
    cycleResetStatus,
    setShowCycleConfirm,
    showCycleConfirm,
    // History
    cycleHistory,
    expandedHistoryId,
    setExpandedHistoryId,
    // Budget actions
    handleAddSpend,
    // UI state
    activeModule,
    setActiveModule,
    // Formatting & utils
    baseCurrencyLabel,
    fmtBase,
    toBase,
    sgdRate,
    lkrRate,
    // Income
    monthlyIncomes, setMonthlyIncomes, toMonthlyBase,
    incomeTotals, budgetedMonthlySpend,
    // Modal controls
    setModalType, setShowAddModal,
    setEditingItem, setShowEditModal, handleDelete,
}) {
    return (
        <>
            {activeModule === 'budget-dashboard' && (
                <div className="section-container">
                    <div className="section-header">
                        <div>
                            <div className="section-title">📊 Budget Overview</div>
                            <div style={{ marginTop: '6px' }}>
                                <span className="cycle-period-pill">📅 {currentCycle.label}</span>
                            </div>
                        </div>
                        {!isNewCycle && (
                            <button
                                className="add-btn"
                                style={{ borderColor: 'rgba(255,215,0,0.25)', fontSize: '12px', padding: '8px 14px' }}
                                onClick={() => setShowCycleConfirm(true)}
                                title="Close this cycle and start a new one"
                            >
                                🔄 New Cycle
                            </button>
                        )}
                    </div>

                    {/* NG-1: New-cycle banner at top of overview */}
                    {isNewCycle && (
                        <CycleResetBanner
                            cycleOpenedAt={cycleOpenedAt}
                            currentCycle={currentCycle}
                            resetStatus={cycleResetStatus}
                            onConfirmClick={() => setShowCycleConfirm(true)}
                        />
                    )}

                    {/* Overview Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                        {/* SG Budget Total */}
                        {(() => {
                            const sgBudget = sgBudgetCycle1.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0)
                                + sgBudgetCycle2.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0);
                            const sgSpent = sgBudgetCycle1.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0)
                                + sgBudgetCycle2.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0);
                            const sgPct = sgBudget > 0 ? Math.min((sgSpent / sgBudget) * 100, 100) : 0;
                            const sgColor = sgPct >= 90 ? 'var(--danger)' : sgPct >= 70 ? '#F59E0B' : '#4CAF50';
                            return (
                                <div className="summary-card" style={{ background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
                                    <div className="summary-card-title">🇸🇬 Singapore Monthly Budget</div>
                                    <div style={{ fontSize: '30px', fontWeight: '700', color: '#4CAF50', marginTop: '12px' }}>
                                        ${sgBudget.toLocaleString()}
                                    </div>
                                    {sgSpent > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                            <div className="budget-util-bar-track" style={{ height: '6px', marginBottom: '6px' }}>
                                                <div className="budget-util-bar-fill" style={{ width: `${sgPct}%`, background: sgColor }} />
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                                <span style={{ color: 'var(--text-tertiary)' }}>Spent: <strong style={{ color: sgColor }}>${sgSpent.toLocaleString()}</strong></span>
                                                <span style={{ fontWeight: '700', color: sgColor }}>{Math.round(sgPct)}%</span>
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                            <span>Cycle 1 (17th):</span>
                                            <span>${sgBudgetCycle1.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0).toLocaleString()}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>Cycle 2 (10th):</span>
                                            <span>${sgBudgetCycle2.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0).toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* LK Budget Total */}
                        {(() => {
                            const lkBudget = lkBudgetDamith.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0)
                                + lkBudgetShan.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0);
                            const lkSpent = lkBudgetDamith.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0)
                                + lkBudgetShan.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0);
                            const lkPct = lkBudget > 0 ? Math.min((lkSpent / lkBudget) * 100, 100) : 0;
                            const lkColor = lkPct >= 90 ? 'var(--danger)' : lkPct >= 70 ? '#F59E0B' : '#2196F3';

                            // Convert LK budget to base currency for comparison if needed
                            const lkBudgetBase = toBase(lkBudget, 'LKR');

                            return (
                                <div className="summary-card" style={{ background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)', border: '1px solid rgba(33, 150, 243, 0.3)' }}>
                                    <div className="summary-card-title">🇱🇰 Sri Lanka Monthly Budget</div>
                                    <div style={{ fontSize: '30px', fontWeight: '700', color: '#2196F3', marginTop: '12px' }}>
                                        ₨{lkBudget.toLocaleString()}
                                    </div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                        ≈ {fmtBase(lkBudgetBase)}
                                    </div>
                                    {lkSpent > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                            <div className="budget-util-bar-track" style={{ height: '6px', marginBottom: '6px' }}>
                                                <div className="budget-util-bar-fill" style={{ width: `${lkPct}%`, background: lkColor }} />
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                                <span style={{ color: 'var(--text-tertiary)' }}>Spent: <strong style={{ color: lkColor }}>₨{lkSpent.toLocaleString()}</strong></span>
                                                <span style={{ fontWeight: '700', color: lkColor }}>{Math.round(lkPct)}%</span>
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                            <span>Budget A:</span>
                                            <span>₨{lkBudgetDamith.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0).toLocaleString()}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>Budget B:</span>
                                            <span>₨{lkBudgetShan.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0).toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    {/* INC-04: Net Cash Flow — Monthly Income − Monthly Spend (base) */}
                    {(() => {
                        const incomeBase = incomeTotals.totalMonthlyBase;
                        const sgSpentBase = toBase(
                            sgBudgetCycle1.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0) +
                            sgBudgetCycle2.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0),
                            'SGD'
                        );
                        const lkSpentBase = toBase(
                            lkBudgetDamith.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0) +
                            lkBudgetShan.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0),
                            'LKR'
                        );
                        const spentBase = sgSpentBase + lkSpentBase;
                        const net = incomeBase - spentBase;
                        const savingsRate = incomeBase > 0 ? (net / incomeBase) * 100 : 0;
                        const netColor = net < 0 ? 'var(--danger)' : savingsRate < 10 ? '#F59E0B' : '#10B981';
                        const accent = netColor;
                        return (
                            <div className="summary-card" style={{
                                marginBottom: '20px',
                                background: `linear-gradient(135deg, ${accent}1a 0%, ${accent}0d 100%)`,
                                border: `1px solid ${accent}55`,
                            }}>
                                <div className="summary-card-title">💵 Monthly Cash Flow</div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginTop: '14px' }}>
                                    <div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Income</div>
                                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#10B981', marginTop: '4px' }}>{fmtBase(incomeBase, { compact: true })}</div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                            {incomeTotals.sourceCount} source{incomeTotals.sourceCount !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Spent</div>
                                        <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--danger)', marginTop: '4px' }}>{fmtBase(spentBase, { compact: true })}</div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>this cycle</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Net</div>
                                        <div style={{ fontSize: '20px', fontWeight: '700', color: netColor, marginTop: '4px' }}>{fmtBase(net, { compact: true })}</div>
                                        <div style={{ fontSize: '11px', color: netColor, marginTop: '2px', fontWeight: 600 }}>
                                            {incomeBase > 0 ? `${Math.round(savingsRate)}% saved` : '—'}
                                        </div>
                                    </div>
                                </div>
                                {incomeBase === 0 && (
                                    <div style={{
                                        marginTop: '12px', padding: '8px 12px',
                                        background: 'rgba(255,255,255,0.04)', borderRadius: '8px',
                                        fontSize: '12px', color: 'var(--text-tertiary)',
                                    }}>
                                        💡 Add your salary or other incomes in the <strong>💰 Income</strong> tab to see your full cash-flow picture.
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* INC-08: Savings Potential — Income − Budgeted Spend (base) */}
                    {(() => {
                        const incomeBase = incomeTotals.totalMonthlyBase;
                        const plannedBase = budgetedMonthlySpend.totalBase;
                        const potential = incomeBase - plannedBase;
                        const potentialPct = incomeBase > 0 ? (potential / incomeBase) * 100 : 0;
                        // Actuals from prior card
                        const sgSpentBase = toBase(
                            sgBudgetCycle1.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0) +
                            sgBudgetCycle2.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0),
                            'SGD'
                        );
                        const lkSpentBase = toBase(
                            lkBudgetDamith.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0) +
                            lkBudgetShan.filter(i => i.enabled).reduce((s, i) => s + (i.actualSpent || 0), 0),
                            'LKR'
                        );
                        const actualSpentBase = sgSpentBase + lkSpentBase;
                        const variance = plannedBase - actualSpentBase; // positive = under-budget
                        const variancePct = plannedBase > 0 ? (variance / plannedBase) * 100 : 0;
                        const accent = potential <= 0 ? 'var(--danger)' : potentialPct < 10 ? '#F59E0B' : '#3B82F6';
                        return (
                            <div className="summary-card" style={{
                                marginBottom: '20px',
                                background: `linear-gradient(135deg, ${accent}1a 0%, ${accent}0d 100%)`,
                                border: `1px solid ${accent}55`,
                            }}>
                                <div className="summary-card-title">🎯 Savings Potential <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: '6px' }}>(if you stick to budget)</span></div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginTop: '14px' }}>
                                    <div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Income</div>
                                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#10B981', marginTop: '4px' }}>{fmtBase(incomeBase, { compact: true })}</div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>per month</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Budgeted</div>
                                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#F59E0B', marginTop: '4px' }}>{fmtBase(plannedBase, { compact: true })}</div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>planned spend</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Potential Save</div>
                                        <div style={{ fontSize: '20px', fontWeight: '700', color: accent, marginTop: '4px' }}>{fmtBase(potential, { compact: true })}</div>
                                        <div style={{ fontSize: '11px', color: accent, marginTop: '2px', fontWeight: 600 }}>
                                            {incomeBase > 0 ? `${Math.round(potentialPct)}% of income` : '—'}
                                        </div>
                                    </div>
                                </div>

                                {/* Variance row: budgeted vs actual */}
                                {plannedBase > 0 && actualSpentBase > 0 && (
                                    <div style={{
                                        marginTop: '14px', padding: '10px 12px',
                                        background: 'rgba(255,255,255,0.04)', borderRadius: '8px',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        fontSize: '12px',
                                    }}>
                                        <span style={{ color: 'var(--text-secondary)' }}>
                                            Actual so far: <strong style={{ color: 'var(--text-primary)' }}>{fmtBase(actualSpentBase, { compact: true })}</strong>
                                        </span>
                                        <span style={{
                                            color: variance >= 0 ? '#10B981' : 'var(--danger)',
                                            fontWeight: 600,
                                        }}>
                                            {variance >= 0 ? '✓ Under by' : '⚠ Over by'} {fmtBase(Math.abs(variance), { compact: true })}
                                            {' '}<span style={{ opacity: 0.7, fontWeight: 400 }}>({Math.abs(Math.round(variancePct))}%)</span>
                                        </span>
                                    </div>
                                )}

                                {incomeBase === 0 && (
                                    <div style={{
                                        marginTop: '12px', padding: '8px 12px',
                                        background: 'rgba(255,255,255,0.04)', borderRadius: '8px',
                                        fontSize: '12px', color: 'var(--text-tertiary)',
                                    }}>
                                        💡 Add incomes in the <strong>💰 Income</strong> tab to see your savings potential.
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* SG Budget Breakdown — with actuals */}
                    <div className="summary-card" style={{ marginBottom: '20px' }}>
                        <div className="summary-card-title">🇸🇬 Singapore Budget Breakdown</div>
                        <div style={{ marginTop: '16px' }}>
                            <div style={{ fontWeight: '600', marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cycle 1 — 17th of Month</div>
                            {sgBudgetCycle1.filter(i => i.enabled).map((item) => {
                                const pct = item.amount > 0 ? Math.min(((item.actualSpent || 0) / item.amount) * 100, 100) : 0;
                                const col = pct >= 90 ? 'var(--danger)' : pct >= 70 ? '#F59E0B' : '#4CAF50';
                                return (
                                    <div key={item.id} style={{ marginBottom: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                                            <span>{item.category}</span>
                                            <span style={{ fontWeight: '600' }}>
                                                {item.actualSpent > 0
                                                    ? <><span style={{ color: col }}>${(item.actualSpent || 0).toLocaleString()}</span><span style={{ color: 'var(--text-tertiary)' }}> / ${item.amount.toLocaleString()}</span></>
                                                    : <span>${item.amount.toLocaleString()}</span>
                                                }
                                            </span>
                                        </div>
                                        {item.amount > 0 && (
                                            <div className="budget-util-bar-track" style={{ height: '3px' }}>
                                                <div className="budget-util-bar-fill" style={{ width: `${pct}%`, background: col }} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <div style={{ borderTop: '2px dashed var(--border)', marginTop: '10px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontWeight: '600', fontSize: '13px' }}>
                                <span>Cycle 1 Total</span>
                                <span style={{ color: '#4CAF50' }}>${sgBudgetCycle1.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0).toLocaleString()}</span>
                            </div>

                            <div style={{ fontWeight: '600', marginTop: '20px', marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cycle 2 — 10th of Month</div>
                            {sgBudgetCycle2.filter(i => i.enabled).map((item) => {
                                const pct = item.amount > 0 ? Math.min(((item.actualSpent || 0) / item.amount) * 100, 100) : 0;
                                const col = pct >= 90 ? 'var(--danger)' : pct >= 70 ? '#F59E0B' : '#4CAF50';
                                return (
                                    <div key={item.id} style={{ marginBottom: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                                            <span>{item.category}</span>
                                            <span style={{ fontWeight: '600' }}>
                                                {item.actualSpent > 0
                                                    ? <><span style={{ color: col }}>${(item.actualSpent || 0).toLocaleString()}</span><span style={{ color: 'var(--text-tertiary)' }}> / ${item.amount.toLocaleString()}</span></>
                                                    : <span>${item.amount.toLocaleString()}</span>
                                                }
                                            </span>
                                        </div>
                                        {item.amount > 0 && (
                                            <div className="budget-util-bar-track" style={{ height: '3px' }}>
                                                <div className="budget-util-bar-fill" style={{ width: `${pct}%`, background: col }} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <div style={{ borderTop: '2px dashed var(--border)', marginTop: '10px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontWeight: '600', fontSize: '13px' }}>
                                <span>Cycle 2 Total</span>
                                <span style={{ color: '#4CAF50' }}>${sgBudgetCycle2.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0).toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    {/* LK Budget Breakdown — with actuals */}
                    <div className="summary-card" style={{ marginBottom: '20px' }}>
                        <div className="summary-card-title">🇱🇰 Sri Lanka Budget Breakdown</div>
                        <div style={{ marginTop: '16px' }}>
                            <div style={{ fontWeight: '600', marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>👤 Budget A</div>
                            {lkBudgetDamith.filter(i => i.enabled).map((item) => {
                                const pct = item.amount > 0 ? Math.min(((item.actualSpent || 0) / item.amount) * 100, 100) : 0;
                                const col = pct >= 90 ? 'var(--danger)' : pct >= 70 ? '#F59E0B' : '#2196F3';
                                return (
                                    <div key={item.id} style={{ marginBottom: '12px', padding: '10px 12px', background: 'rgba(33, 150, 243, 0.05)', borderRadius: '8px', border: '1px solid rgba(33, 150, 243, 0.15)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                            <span style={{ fontWeight: '600', fontSize: '13px' }}>{item.category}</span>
                                            <span style={{ fontWeight: '600', fontSize: '13px' }}>
                                                {item.actualSpent > 0
                                                    ? <><span style={{ color: col }}>₨{(item.actualSpent || 0).toLocaleString()}</span><span style={{ color: 'var(--text-tertiary)', fontWeight: '400' }}> / ₨{item.amount.toLocaleString()}</span></>
                                                    : <span style={{ color: '#2196F3' }}>₨{item.amount.toLocaleString()}</span>
                                                }
                                            </span>
                                        </div>
                                        {item.amount > 0 && (
                                            <div className="budget-util-bar-track" style={{ height: '3px', marginBottom: '6px' }}>
                                                <div className="budget-util-bar-fill" style={{ width: `${pct}%`, background: col }} />
                                            </div>
                                        )}
                                        {item.description && <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{item.description}</div>}
                                    </div>
                                );
                            })}
                            <div style={{ borderTop: '2px dashed var(--border)', marginTop: '6px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontWeight: '600', fontSize: '13px' }}>
                                <span>Budget A Total</span>
                                <span style={{ color: '#2196F3' }}>₨{lkBudgetDamith.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0).toLocaleString()}</span>
                            </div>

                            <div style={{ fontWeight: '600', marginTop: '20px', marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>👤 Budget B</div>
                            {lkBudgetShan.filter(i => i.enabled).map((item) => {
                                const pct = item.amount > 0 ? Math.min(((item.actualSpent || 0) / item.amount) * 100, 100) : 0;
                                const col = pct >= 90 ? 'var(--danger)' : pct >= 70 ? '#F59E0B' : '#E91E63';
                                return (
                                    <div key={item.id} style={{ marginBottom: '12px', padding: '10px 12px', background: 'rgba(233, 30, 99, 0.05)', borderRadius: '8px', border: '1px solid rgba(233, 30, 99, 0.15)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                            <span style={{ fontWeight: '600', fontSize: '13px' }}>{item.category}</span>
                                            <span style={{ fontWeight: '600', fontSize: '13px' }}>
                                                {item.actualSpent > 0
                                                    ? <><span style={{ color: col }}>₨{(item.actualSpent || 0).toLocaleString()}</span><span style={{ color: 'var(--text-tertiary)', fontWeight: '400' }}> / ₨{item.amount.toLocaleString()}</span></>
                                                    : <span style={{ color: '#E91E63' }}>₨{item.amount.toLocaleString()}</span>
                                                }
                                            </span>
                                        </div>
                                        {item.amount > 0 && (
                                            <div className="budget-util-bar-track" style={{ height: '3px', marginBottom: '6px' }}>
                                                <div className="budget-util-bar-fill" style={{ width: `${pct}%`, background: col }} />
                                            </div>
                                        )}
                                        {item.description && <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{item.description}</div>}
                                    </div>
                                );
                            })}
                            <div style={{ borderTop: '2px dashed var(--border)', marginTop: '6px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontWeight: '600', fontSize: '13px' }}>
                                <span>Budget B Total</span>
                                <span style={{ color: '#E91E63' }}>₨{lkBudgetShan.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0).toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    {/* Recent cycle history teaser — full view is in History tab */}
                    {cycleHistory.length > 0 && (
                        <div className="summary-card">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <div className="summary-card-title" style={{ marginBottom: 0 }}>📁 Recent Cycles</div>
                                <button
                                    onClick={() => setActiveModule('budget-history')}
                                    style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                                >
                                    View All →
                                </button>
                            </div>
                            {cycleHistory.slice(0, 3).map((snap, i) => {
                                const cycleId = snap.cycleId || snap.period || '';
                                const label = snap.label || formatCycleId(cycleId);
                                const allItems = [...(snap.sgCycle1 || []), ...(snap.sgCycle2 || []), ...(snap.lkA || []), ...(snap.lkB || [])].filter(x => x.enabled && (x.amount || 0) > 0);
                                const totalBudget = allItems.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
                                const totalSpent = allItems.reduce((s, x) => s + (x.actualSpent || 0), 0);
                                const pct = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0;
                                const col = pct > 100 ? '#EF4444' : pct >= 90 ? '#F59E0B' : '#10B981';
                                return (
                                    <div key={cycleId || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < Math.min(cycleHistory.length, 3) - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                                        <div>
                                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
                                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                {snap.driveExported && <span style={{ color: '#6ee7b7' }}>☁️ Drive</span>}
                                                {snap.closedAt && <span>Closed {new Date(snap.closedAt).toLocaleDateString()}</span>}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            {totalBudget > 0 && (
                                                <>
                                                    <div style={{ fontSize: '14px', fontWeight: 700, color: col }}>{pct}%</div>
                                                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>utilised</div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {activeModule === 'sg-budget' && (
                <div className="section-container">
                    <div className="section-header">
                        <div>
                            <div className="section-title">🇸🇬 Singapore Budget</div>
                            <div style={{ marginTop: '6px' }}>
                                <span className="cycle-period-pill">📅 {currentCycle.label}</span>
                            </div>
                        </div>
                        {!isNewCycle && (
                            <button
                                className="add-btn"
                                style={{ borderColor: 'rgba(255,215,0,0.25)', fontSize: '12px', padding: '8px 14px' }}
                                onClick={() => setShowCycleConfirm(true)}
                            >
                                🔄 New Cycle
                            </button>
                        )}
                    </div>

                    {/* NG-1: New-cycle banner */}
                    {isNewCycle && (
                        <CycleResetBanner
                            cycleOpenedAt={cycleOpenedAt}
                            currentCycle={currentCycle}
                            resetStatus={cycleResetStatus}
                            onConfirmClick={() => setShowCycleConfirm(true)}
                        />
                    )}

                    {/* NG-1: Cycle 1 utilisation card */}
                    <CycleUtilisationCard items={sgBudgetCycle1} symbol="$" accent="#4CAF50" />

                    <BudgetGroup
                        title="Cycle 1 — 17th of Month"
                        items={sgBudgetCycle1}
                        accent="#4CAF50"
                        symbol="$"
                        hasDesc={false}
                        onAdd={() => setSgBudgetCycle1([...sgBudgetCycle1, { id: Date.now(), category: 'New Item', amount: 0, enabled: true, actualSpent: 0, spendLog: [] }])}
                        onUpdate={(i, updated) => { const a = [...sgBudgetCycle1]; a[i] = updated; setSgBudgetCycle1(a); }}
                        onDelete={(i) => setSgBudgetCycle1(sgBudgetCycle1.filter((_, idx) => idx !== i))}
                        onAddSpend={(itemId, amount, note) => handleAddSpend(setSgBudgetCycle1, sgBudgetCycle1, itemId, amount, note)}
                    />

                    {/* NG-1: Cycle 2 utilisation card */}
                    <CycleUtilisationCard items={sgBudgetCycle2} symbol="$" accent="#4CAF50" />

                    <BudgetGroup
                        title="Cycle 2 — 10th of Month"
                        items={sgBudgetCycle2}
                        accent="#4CAF50"
                        symbol="$"
                        hasDesc={false}
                        onAdd={() => setSgBudgetCycle2([...sgBudgetCycle2, { id: Date.now(), category: 'New Item', amount: 0, enabled: true, actualSpent: 0, spendLog: [] }])}
                        onUpdate={(i, updated) => { const a = [...sgBudgetCycle2]; a[i] = updated; setSgBudgetCycle2(a); }}
                        onDelete={(i) => setSgBudgetCycle2(sgBudgetCycle2.filter((_, idx) => idx !== i))}
                        onAddSpend={(itemId, amount, note) => handleAddSpend(setSgBudgetCycle2, sgBudgetCycle2, itemId, amount, note)}
                    />

                    <div className="budget-grand-total">
                        <span className="budget-grand-label">Monthly Total</span>
                        <span className="budget-grand-value">
                            ${(
                                sgBudgetCycle1.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0) +
                                sgBudgetCycle2.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0)
                            ).toLocaleString()}
                        </span>
                    </div>
                </div>
            )}

            {activeModule === 'lk-budget' && (
                <div className="section-container">
                    <div className="section-header">
                        <div>
                            <div className="section-title">🇱🇰 Sri Lanka Budget</div>
                            <div style={{ marginTop: '6px' }}>
                                <span className="cycle-period-pill">📅 {currentCycle.label}</span>
                            </div>
                        </div>
                        {!isNewCycle && (
                            <button
                                className="add-btn"
                                style={{ borderColor: 'rgba(255,215,0,0.25)', fontSize: '12px', padding: '8px 14px' }}
                                onClick={() => setShowCycleConfirm(true)}
                            >
                                🔄 New Cycle
                            </button>
                        )}
                    </div>

                    {/* NG-1: New-cycle banner (shared across all budgets) */}
                    {isNewCycle && (
                        <CycleResetBanner
                            cycleOpenedAt={cycleOpenedAt}
                            currentCycle={currentCycle}
                            resetStatus={cycleResetStatus}
                            onConfirmClick={() => setShowCycleConfirm(true)}
                        />
                    )}

                    {/* NG-1: Budget A utilisation card */}
                    <CycleUtilisationCard items={lkBudgetDamith} symbol="₨" accent="#2196F3" />

                    <BudgetGroup
                        title="Budget A"
                        items={lkBudgetDamith}
                        accent="#2196F3"
                        symbol="₨"
                        hasDesc={true}
                        onAdd={() => setLkBudgetDamith([...lkBudgetDamith, { id: Date.now(), category: 'New Category', description: '', amount: 0, enabled: true, actualSpent: 0, spendLog: [] }])}
                        onUpdate={(i, updated) => { const a = [...lkBudgetDamith]; a[i] = updated; setLkBudgetDamith(a); }}
                        onDelete={(i) => setLkBudgetDamith(lkBudgetDamith.filter((_, idx) => idx !== i))}
                        onAddSpend={(itemId, amount, note) => handleAddSpend(setLkBudgetDamith, lkBudgetDamith, itemId, amount, note)}
                    />

                    {/* NG-1: Budget B utilisation card */}
                    <CycleUtilisationCard items={lkBudgetShan} symbol="₨" accent="#E91E63" />

                    <BudgetGroup
                        title="Budget B"
                        items={lkBudgetShan}
                        accent="#E91E63"
                        symbol="₨"
                        hasDesc={true}
                        onAdd={() => setLkBudgetShan([...lkBudgetShan, { id: Date.now(), category: 'New Category', description: '', amount: 0, enabled: true, actualSpent: 0, spendLog: [] }])}
                        onUpdate={(i, updated) => { const a = [...lkBudgetShan]; a[i] = updated; setLkBudgetShan(a); }}
                        onDelete={(i) => setLkBudgetShan(lkBudgetShan.filter((_, idx) => idx !== i))}
                        onAddSpend={(itemId, amount, note) => handleAddSpend(setLkBudgetShan, lkBudgetShan, itemId, amount, note)}
                    />

                    <div className="budget-grand-total">
                        <span className="budget-grand-label">Grand Total</span>
                        <span className="budget-grand-value" style={{ color: '#4CAF50' }}>
                            ₨{(
                                lkBudgetDamith.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0) +
                                lkBudgetShan.filter(i => i.enabled).reduce((s, i) => s + (i.amount || 0), 0)
                            ).toLocaleString()}
                        </span>
                    </div>
                </div>
            )}

            {/* INC-05: Income management screen */}
            {activeModule === 'budget-income' && (
                <div className="section-container">
                    <div className="section-header">
                        <div>
                            <div className="section-title">💰 Monthly Income</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                                Track salary, freelance, dividends, rental & other recurring income
                            </div>
                        </div>
                        <button
                            className="add-btn"
                            onClick={() => { setModalType('income'); setShowAddModal(true); }}
                        >
                            + Add Income
                        </button>
                    </div>

                    {/* Hero card: total monthly income (base) */}
                    <div className="summary-card" style={{
                        marginBottom: '20px',
                        background: 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0.06) 100%)',
                        border: '1px solid rgba(16,185,129,0.4)',
                    }}>
                        <div className="summary-card-title">Total Monthly Income</div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: '#10B981', marginTop: '8px' }}>
                            {fmtBase(incomeTotals.totalMonthlyBase)}
                        </div>
                        <div style={{ display: 'flex', gap: '20px', marginTop: '12px', flexWrap: 'wrap' }}>
                            <div>
                                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>Annualised</div>
                                <div style={{ fontSize: '16px', fontWeight: '600' }}>{fmtBase(incomeTotals.annualBase, { compact: true })}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>Active Sources</div>
                                <div style={{ fontSize: '16px', fontWeight: '600' }}>{incomeTotals.sourceCount}</div>
                            </div>
                            {(() => {
                                const potential = incomeTotals.totalMonthlyBase - budgetedMonthlySpend.totalBase;
                                const pct = incomeTotals.totalMonthlyBase > 0
                                    ? (potential / incomeTotals.totalMonthlyBase) * 100 : 0;
                                const col = potential <= 0 ? 'var(--danger)' : pct < 10 ? '#F59E0B' : '#3B82F6';
                                return (
                                    <div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>Potential Monthly Save</div>
                                        <div style={{ fontSize: '16px', fontWeight: '600', color: col }}>
                                            {fmtBase(potential, { compact: true })}
                                            {incomeTotals.totalMonthlyBase > 0 && (
                                                <span style={{ fontSize: '11px', fontWeight: 400, marginLeft: '6px', opacity: 0.8 }}>
                                                    ({Math.round(pct)}%)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Per-category breakdown */}
                    {incomeTotals.byCategory.length > 0 && (
                        <div className="summary-card" style={{ marginBottom: '20px' }}>
                            <div className="summary-card-title">By Category</div>
                            <div style={{ marginTop: '12px' }}>
                                {incomeTotals.byCategory.map(cat => {
                                    const pct = incomeTotals.totalMonthlyBase > 0
                                        ? (cat.monthlyBase / incomeTotals.totalMonthlyBase) * 100 : 0;
                                    return (
                                        <div key={cat.category} style={{ marginBottom: '12px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                                                <span style={{ textTransform: 'capitalize' }}>{cat.category} <span style={{ color: 'var(--text-tertiary)' }}>({cat.count})</span></span>
                                                <span style={{ fontWeight: 600 }}>{fmtBase(cat.monthlyBase, { compact: true })} <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>· {Math.round(pct)}%</span></span>
                                            </div>
                                            <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                                                <div style={{ width: `${pct}%`, height: '100%', background: '#10B981' }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Income list */}
                    {monthlyIncomes.length === 0 ? (
                        <div className="empty-state" style={{ padding: '40px 20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '40px', marginBottom: '12px' }}>💸</div>
                            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>No income sources yet</div>
                            <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '16px' }}>
                                Add your salary, freelance gigs, rental income, dividends, etc.
                            </div>
                            <button
                                className="add-btn"
                                onClick={() => { setModalType('income'); setShowAddModal(true); }}
                            >
                                + Add your first income
                            </button>
                        </div>
                    ) : (
                        <div className="list-container">
                            {monthlyIncomes.map((inc) => {
                                const monthlyBase = toMonthlyBase(inc);
                                const freqLabel = inc.frequency === 'monthly' ? 'Monthly'
                                    : inc.frequency === 'quarterly' ? 'Quarterly'
                                        : inc.frequency === 'annual' ? 'Annual'
                                            : inc.frequency === 'one-time' ? 'One-time' : inc.frequency;
                                const symbol = inc.currency === 'LKR' ? '₨' : inc.currency === 'USD' ? '$' : 'S$';
                                return (
                                    <div
                                        key={inc.id}
                                        className="list-item"
                                        style={{ opacity: inc.enabled === false ? 0.5 : 1 }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <span style={{ fontWeight: 600 }}>{inc.name || 'Untitled'}</span>
                                                {inc.category && (
                                                    <span style={{
                                                        fontSize: '10px', padding: '2px 8px', borderRadius: '10px',
                                                        background: 'rgba(16,185,129,0.15)', color: '#10B981',
                                                        textTransform: 'capitalize', fontWeight: 500,
                                                    }}>{inc.category}</span>
                                                )}
                                                <span style={{
                                                    fontSize: '10px', padding: '2px 8px', borderRadius: '10px',
                                                    background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)',
                                                }}>{freqLabel}</span>
                                                {inc.enabled === false && (
                                                    <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)', color: 'var(--text-tertiary)' }}>Paused</span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                                                {symbol}{(parseFloat(inc.amount) || 0).toLocaleString()} · {inc.currency || 'SGD'}
                                                {inc.frequency !== 'monthly' && inc.frequency !== 'one-time' && (
                                                    <> · {fmtBase(monthlyBase, { compact: true })}/mo</>
                                                )}
                                                {inc.notes && <> · {inc.notes}</>}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right', marginRight: '12px' }}>
                                            <div style={{ fontWeight: 700, color: '#10B981' }}>{fmtBase(monthlyBase, { compact: true })}</div>
                                            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>per month</div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button
                                                className="icon-btn"
                                                title={inc.enabled === false ? 'Resume' : 'Pause'}
                                                onClick={() => setMonthlyIncomes(monthlyIncomes.map(i => i.id === inc.id ? { ...i, enabled: i.enabled === false } : i))}
                                            >
                                                {inc.enabled === false ? '▶' : '⏸'}
                                            </button>
                                            <button
                                                className="icon-btn"
                                                title="Edit"
                                                onClick={() => { setModalType('income'); setEditingItem(inc); setShowEditModal(true); }}
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                className="icon-btn"
                                                title="Delete"
                                                onClick={() => { setModalType('income'); handleDelete(inc); }}
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {activeModule === 'budget-history' && (
                <div className="section-container">
                    <div className="section-header">
                        <div>
                            <div className="section-title">📁 Budget History</div>
                            <div className="section-subtitle">{cycleHistory.length} archived cycle{cycleHistory.length !== 1 ? 's' : ''}</div>
                        </div>
                        {cycleHistory.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textAlign: 'right' }}>
                                    ☁️ {cycleHistory.filter(s => s.driveExported).length}/{cycleHistory.length} on Drive
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Info banner about Drive exports */}
                    {cycleHistory.length > 0 && (
                        <div style={{
                            background: 'rgba(16,185,129,0.07)',
                            border: '1px solid rgba(16,185,129,0.15)',
                            borderRadius: '12px',
                            padding: '10px 14px',
                            marginBottom: '16px',
                            fontSize: '12px',
                            color: 'var(--text-secondary)',
                            lineHeight: '1.6',
                        }}>
                            📂 Drive exports saved to: <code style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: '4px', fontSize: '11px' }}>Application/iPlanner/iplanner-budget-YYYY-MM.json</code>
                        </div>
                    )}

                    <BudgetHistoryTab
                        cycleHistory={cycleHistory}
                        expandedId={expandedHistoryId}
                        onToggleExpand={(id) => setExpandedHistoryId(prev => prev === id ? null : id)}
                    />
                </div>
            )}
        </>
    );
}

// =============================================
// BUDGET CYCLE RESET BANNER COMPONENT
// =============================================
export function CycleResetBanner({ cycleOpenedAt, currentCycle, resetStatus, onConfirmClick }) {
    const closingLabel = cycleOpenedAt ? (() => {
        const [y, m] = cycleOpenedAt.split('-').map(Number);
        const pad = n => String(n).padStart(2, '0');
        const start = new Date(y, m - 1, 16);
        let ey = y, em = m; em += 1;
        if (em > 12) { em = 1; ey += 1; }
        const end = new Date(ey, em - 1, 15);
        const fmt = { day: 'numeric', month: 'short' };
        return `${start.toLocaleDateString(undefined, fmt)} – ${end.toLocaleDateString(undefined, { ...fmt, year: 'numeric' })}`;
    })() : '';

    return (
        <div className="cycle-reset-banner">
            <div className="cycle-reset-banner-title">
                <span>🔄</span> New Cycle Ready
            </div>
            <div className="cycle-reset-banner-sub">
                Cycle <strong style={{ color: 'var(--text-primary)' }}>{closingLabel}</strong> has ended.
                The new cycle <strong style={{ color: 'var(--text-primary)' }}>{currentCycle.label}</strong> is ready to begin.
                <br />Resetting will export your actuals to Google Drive and clear all spend logs.
            </div>
            <div className="cycle-reset-banner-actions">
                <button
                    className="cycle-reset-confirm-btn"
                    onClick={onConfirmClick}
                    disabled={resetStatus === 'exporting'}
                >
                    {resetStatus === 'exporting' ? (
                        <><span>⏳</span> Exporting…</>
                    ) : resetStatus === 'done' ? (
                        <><span>✅</span> Done!</>
                    ) : resetStatus === 'error' ? (
                        <><span>⚠️</span> Reset Anyway</>
                    ) : (
                        <><span>☁️</span> Export & Start New Cycle</>
                    )}
                </button>
            </div>
            {resetStatus === 'done' && (
                <div className="cycle-reset-status">✅ Exported to Drive — cycle reset complete</div>
            )}
            {resetStatus === 'error' && (
                <div className="cycle-reset-status" style={{ color: '#fca5a5' }}>⚠️ Drive export failed — actuals still archived locally</div>
            )}
        </div>
    );
}

// =============================================
// BUDGET HISTORY TAB COMPONENT
// =============================================
export function BudgetHistoryTab({ cycleHistory, expandedId, onToggleExpand }) {
    if (!cycleHistory || cycleHistory.length === 0) {
        return (
            <div className="budget-history-empty">
                <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.2 }}>📁</div>
                <div>No archived cycles yet.</div>
                <div style={{ fontSize: '12px', marginTop: '6px', color: 'var(--text-tertiary)' }}>
                    When you close a cycle it will appear here with full spend details.
                </div>
            </div>
        );
    }

    const pctColor = p => p > 100 ? 'var(--danger)' : p >= 90 ? '#F59E0B' : 'var(--secondary)';

    const GroupSection = ({ title, items, symbol, accent }) => {
        const enabled = (items || []).filter(i => i.enabled && (i.amount || 0) > 0);
        if (enabled.length === 0) return null;
        const totalBudget = enabled.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
        const totalSpent = enabled.reduce((s, i) => s + (i.actualSpent || 0), 0);
        const totalPct = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0;
        return (
            <div>
                <div className="budget-history-group-title">{title}</div>
                {enabled.map((item, idx) => {
                    const budget = parseFloat(item.amount) || 0;
                    const spent = item.actualSpent || 0;
                    const pct = budget > 0 ? Math.round((spent / budget) * 100) : 0;
                    const col = pctColor(pct);
                    return (
                        <div key={idx}>
                            <div className="budget-history-item-row">
                                <span className="bhi-category">{item.category}</span>
                                <span className="bhi-budget" style={{ color: 'var(--text-tertiary)' }}>{symbol}{budget.toLocaleString()}</span>
                                <span className="bhi-spent" style={{ color: col }}>{symbol}{spent.toLocaleString()}</span>
                                <span className="bhi-pct" style={{ color: col }}>{pct}%</span>
                            </div>
                            <div className="bhi-bar-row">
                                <div className="budget-util-bar-track" style={{ height: '3px' }}>
                                    <div className="budget-util-bar-fill" style={{ width: `${Math.min(100, pct)}%`, background: col }} />
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div className="budget-history-totals">
                    <span className="bht-label">Total</span>
                    <div className="bht-values">
                        <span style={{ color: 'var(--text-tertiary)' }}>Budget: <strong>{symbol}{totalBudget.toLocaleString()}</strong></span>
                        <span style={{ color: pctColor(totalPct) }}>Spent: <strong>{symbol}{totalSpent.toLocaleString()}</strong></span>
                        <span style={{ color: pctColor(totalPct), fontWeight: 700 }}>{totalPct}%</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            {cycleHistory.map((snap, i) => {
                // Support both old format (period) and new format (cycleId + label)
                const cycleId = snap.cycleId || snap.period || '';
                const label = snap.label || (snap.period ? formatCycleId(snap.period) + ' cycle' : cycleId);
                const dateStr = snap.cycleStart && snap.cycleEnd
                    ? `${snap.cycleStart} → ${snap.cycleEnd}`
                    : (snap.closedAt ? `Closed ${new Date(snap.closedAt).toLocaleDateString()}` : '');
                const isOpen = expandedId === cycleId;

                // Aggregate totals for score pill
                const allItems = [
                    ...(snap.sgCycle1 || []), ...(snap.sgCycle2 || []),
                    ...(snap.lkA || []), ...(snap.lkB || []),
                ].filter(x => x.enabled && (x.amount || 0) > 0);
                const totalBudget = allItems.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
                const totalSpent = allItems.reduce((s, x) => s + (x.actualSpent || 0), 0);
                const pct = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0;
                const pillColor = pct > 100 ? '#EF4444' : pct >= 90 ? '#F59E0B' : '#10B981';
                const pillBg = pct > 100 ? 'rgba(239,68,68,0.12)' : pct >= 90 ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)';

                return (
                    <div key={cycleId || i} className="budget-history-card">
                        <div className="budget-history-card-header" onClick={() => onToggleExpand(cycleId)}>
                            <div>
                                <div className="budget-history-period">{label}</div>
                                <div className="budget-history-dates">{dateStr}</div>
                            </div>
                            <div className="budget-history-meta">
                                {snap.driveExported === true && (
                                    <span className="drive-export-badge">☁️ Drive</span>
                                )}
                                {snap.driveExported === false && snap.driveExportError && (
                                    <span className="drive-export-badge failed">⚠️ Local only</span>
                                )}
                                {totalBudget > 0 && (
                                    <span className="budget-history-score-pill" style={{ color: pillColor, background: pillBg }}>
                                        {pct}%
                                    </span>
                                )}
                                <span style={{ color: 'var(--text-tertiary)', fontSize: '16px' }}>{isOpen ? '▲' : '▼'}</span>
                            </div>
                        </div>

                        {isOpen && (
                            <div className="budget-history-body">
                                {/* Column headers */}
                                <div className="budget-history-item-row" style={{ paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</span>
                                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Budget</span>
                                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Spent</span>
                                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.5px' }}>%</span>
                                </div>

                                <GroupSection title="🇸🇬 SG Cycle 1" items={snap.sgCycle1} symbol="$" accent="#4CAF50" />
                                <GroupSection title="🇸🇬 SG Cycle 2" items={snap.sgCycle2} symbol="$" accent="#4CAF50" />
                                <GroupSection title="🇱🇰 LK Budget A" items={snap.lkA} symbol="₨" accent="#2196F3" />
                                <GroupSection title="🇱🇰 LK Budget B" items={snap.lkB} symbol="₨" accent="#E91E63" />

                                {snap.driveExported === false && snap.driveExportError && (
                                    <div style={{ marginTop: '12px', padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', fontSize: '11px', color: '#fca5a5' }}>
                                        ⚠️ Drive export failed: {snap.driveExportError}
                                    </div>
                                )}
                                {snap.closedAt && (
                                    <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--text-tertiary)', textAlign: 'right' }}>
                                        Archived {new Date(snap.closedAt).toLocaleString()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}